import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  Alert,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  StyleSheet,
  Platform,
} from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { globalStyles } from '../styles/globalStyles';
import { venueListStyles } from '../styles/venueListStyles';
import { homeStyles } from '../styles/HomeStyles';
import { getResponsiveValue } from '../styles/breakpoints';
import { SearchBar } from '../components/SearchBar';
import { useUser } from '../components/LoginPersistence';
import { EventPostCard } from '../components/events/EventPostCard';
import { EventPostFormModal } from '../components/events/EventPostFormModal';
import { deleteEventPost, subscribeEventPosts } from '../utils/eventPostsService';
import { isSignedInAccount } from '../utils/liveTechSpecs';
import { canManageEventPost } from '../utils/eventPostAccess';

const EVENTS_BACKGROUND = '#231f20';
/** Text, search bar, filters and count on the dark Events page. */
const EVENTS_ACCENT = '#f5f1ea';

const MEDIA_FILTERS = [
  { id: 'videos', label: 'VIDEOS' },
  { id: 'photos', label: 'PHOTOS' },
];

export default function EventsList() {
  const route = useRoute();
  const navigation = useNavigation();
  const [searchQuery, setSearchQuery] = useState(route.params?.searchQuery || '');
  const [mediaFilters, setMediaFilters] = useState({ videos: true, photos: true });
  const { user: currentUser } = useUser();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPostForm, setShowPostForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => subscribeEventPosts(
    (posts) => { setEvents(posts); setLoading(false); },
    (err) => { console.warn('Event posts', err); setLoading(false); },
  ), []);

  const toggleMediaFilter = (id) => {
    setMediaFilters((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const displayList = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return events.filter((item) => {
      if (item.mediaType && mediaFilters[item.mediaType] === false) return false;
      if (!q) return true;
      const websites = (item.websites || []).map((w) => `${w.text || ''} ${w.url || ''}`).join(' ');
      return `${item.title || ''} ${item.details || ''} ${item.venueName || ''} ${item.organizer?.name || ''} ${item.username || ''} ${websites}`
        .toLowerCase()
        .includes(q);
    });
  }, [events, mediaFilters, searchQuery]);

  const handlePostPress = () => {
    if (!isSignedInAccount(currentUser)) {
      navigation.navigate('Login');
      return;
    }
    setShowPostForm(true);
  };

  const handleEditEvent = (event) => {
    if (!canManageEventPost(event, currentUser)) return;
    setEditingEvent(event);
    setShowPostForm(true);
  };

  const handleDeleteEvent = (event) => {
    if (!canManageEventPost(event, currentUser)) return;
    setDeleteError('');
    setDeleteTarget(event);
  };

  const confirmDeleteEvent = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const cleanupErrors = await deleteEventPost(deleteTarget);
      setDeleteTarget(null);
      if (cleanupErrors.length) {
        console.error('Event deleted, but some event media could not be removed:', cleanupErrors);
        Alert.alert('Event deleted', 'The event and its chat were deleted, but some attached media could not be removed.');
      }
    } catch (error) {
      console.error('Delete event failed', error);
      setDeleteError('Could not delete the event. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const tabTextStyle = (active) => [
    homeStyles.homeSubtitle2,
    { color: EVENTS_ACCENT },
    active && { textDecorationLine: 'underline' },
  ];
  const webPointer = Platform.OS === 'web' ? { cursor: 'pointer' } : {};
  const searchParams = searchQuery ? { searchQuery } : undefined;

  return (
    <View style={[globalStyles.container, { paddingTop: 80, backgroundColor: EVENTS_BACKGROUND }]}>
      <View style={[globalStyles.overlay, { backgroundColor: EVENTS_BACKGROUND }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 60, marginBottom: 8 }}>
          <TouchableOpacity onPress={() => navigation.navigate('CommunityList', searchParams)} activeOpacity={0.8} style={webPointer}>
            <Text style={tabTextStyle(false)}>Directory</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => {}} activeOpacity={0.8} style={webPointer}>
            <Text style={tabTextStyle(true)}>Events</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => navigation.navigate('GearSwapList', searchParams)} activeOpacity={0.8} style={webPointer}>
            <Text style={tabTextStyle(false)}>Gear Swap</Text>
          </TouchableOpacity>
        </View>

        <View style={[venueListStyles.searchContainer, { borderBottomColor: 'rgba(245,241,234,0.3)', backgroundColor: 'transparent' }]}>
          <SearchBar
            nativeID="community-events-search"
            color={EVENTS_ACCENT}
            placeholderColor="rgba(245, 241, 234, 0.6)"
            value={searchQuery}
            onChangeText={setSearchQuery}
            onSubmit={() => {}}
            placeholder="Search events..."
          />
        </View>

        <View style={styles.mediaFilterRow}>
          {MEDIA_FILTERS.map(({ id, label }) => {
            const checked = mediaFilters[id];
            return (
              <TouchableOpacity
                key={id}
                style={[styles.mediaFilterChip, checked && styles.mediaFilterChipChecked]}
                onPress={() => toggleMediaFilter(id)}
                activeOpacity={0.8}
                accessibilityRole="checkbox"
                accessibilityState={{ checked }}
              >
                <View style={[styles.mediaFilterCheckbox, checked && styles.mediaFilterCheckboxChecked]}>
                  {checked ? <Ionicons name="checkmark" size={14} color={EVENTS_BACKGROUND} /> : null}
                </View>
                <Text style={[styles.mediaFilterLabel, { color: checked ? EVENTS_BACKGROUND : EVENTS_ACCENT }]}>{label}</Text>
              </TouchableOpacity>
            );
          })}
        </View>

        <View style={styles.listSection}>
          <View style={venueListStyles.venueListHeaderSection}>
            <View style={{ flex: 1 }} />
            <TouchableOpacity
              style={[venueListStyles.postButtonContainer, { flex: 1 }]}
              onPress={handlePostPress}
              activeOpacity={0.8}
            >
              <Text style={[venueListStyles.postButton, { color: EVENTS_ACCENT }]}>POST</Text>
            </TouchableOpacity>
            <View style={[venueListStyles.venueCountContainer, { flex: 1 }]}>
              <Text style={[venueListStyles.venueCountText, { color: EVENTS_ACCENT }]}>
                {displayList.length} {displayList.length === 1 ? 'event' : 'events'} found
              </Text>
            </View>
          </View>

          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={EVENTS_ACCENT} />
              <Text style={styles.loadingText}>Loading events...</Text>
            </View>
          ) : displayList.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>No events yet</Text>
              <Text style={styles.emptySubtext}>
                {searchQuery ? 'Try a different search term' : 'Event posts will appear here. Check back soon!'}
              </Text>
            </View>
          ) : (
            <View style={styles.communityGrid}>
              {displayList.map((event) => (
                <EventPostCard
                  key={event.id}
                  event={event}
                  currentUser={currentUser}
                  onEdit={handleEditEvent}
                  onDelete={handleDeleteEvent}
                />
              ))}
            </View>
          )}
        </View>
      </View>

      <EventPostFormModal
        visible={showPostForm}
        currentUser={currentUser}
        event={editingEvent}
        onClose={() => { setShowPostForm(false); setEditingEvent(null); }}
        onPosted={() => { setShowPostForm(false); setEditingEvent(null); }}
      />

      <Modal
        visible={Boolean(deleteTarget)}
        transparent
        animationType="fade"
        onRequestClose={() => { if (!deleting) setDeleteTarget(null); }}
      >
        <View style={styles.confirmBackdrop}>
          <View style={styles.confirmCard}>
            <Text style={styles.confirmTitle}>Delete event?</Text>
            <Text style={styles.confirmMessage}>
              This will permanently delete “{deleteTarget?.title || 'this event'}” and its event chat.
            </Text>
            {deleteError ? <Text style={styles.confirmError}>{deleteError}</Text> : null}
            <View style={styles.confirmActions}>
              <TouchableOpacity
                onPress={() => setDeleteTarget(null)}
                disabled={deleting}
                style={[styles.confirmButton, styles.cancelDeleteButton]}
              >
                <Text style={styles.cancelDeleteText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={confirmDeleteEvent}
                disabled={deleting}
                style={[styles.confirmButton, styles.confirmDeleteButton, deleting && { opacity: 0.6 }]}
              >
                {deleting
                  ? <ActivityIndicator size="small" color="#fff" />
                  : <Text style={styles.confirmDeleteText}>Delete</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  mediaFilterRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  mediaFilterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: EVENTS_ACCENT,
  },
  mediaFilterChipChecked: {
    backgroundColor: EVENTS_ACCENT,
  },
  mediaFilterCheckbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: EVENTS_ACCENT,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mediaFilterCheckboxChecked: {
    borderColor: EVENTS_BACKGROUND,
  },
  mediaFilterLabel: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: '600',
  },
  listSection: {
    width: '100%',
    alignSelf: 'stretch',
  },
  communityGrid: {
    width: '100%',
    maxWidth: 1400,
    alignSelf: 'center',
    marginTop: 35,
    display: Platform.OS === 'web' ? 'grid' : 'flex',
    ...(Platform.OS === 'web' && {
      gridTemplateColumns: getResponsiveValue({
        mobile: 'repeat(1, 1fr)',
        tablet: 'repeat(2, 1fr)',
        laptop: 'repeat(3, 1fr)',
        desktop: 'repeat(3, 1fr)',
      }),
      // Same grid as Gear Swap; 'start' so opening one card doesn't re-center its row.
      gap: getResponsiveValue({
        mobile: 15,
        tablet: 15,
        laptop: 25,
        desktop: 30
      }),
      alignItems: 'start',
      justifyItems: getResponsiveValue({
        mobile: 'center',
        tablet: 'stretch',
        laptop: 'stretch',
        desktop: 'stretch'
      }),
      paddingHorizontal: getResponsiveValue({
        mobile: 0,
        tablet: 20,
        laptop: 20,
        desktop: 20
      }),
      marginBottom: getResponsiveValue({
        mobile: 20,
        tablet: 30,
        laptop: 40,
        desktop: 40
      }),
    }),
    ...(Platform.OS !== 'web' && {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingBottom: 20,
    }),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    minHeight: 200,
  },
  loadingText: {
    color: EVENTS_ACCENT,
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    marginTop: 15,
    textAlign: 'center',
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    minHeight: 200,
  },
  emptyText: {
    color: EVENTS_ACCENT,
    fontSize: 18,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    marginBottom: 10,
    textAlign: 'center',
  },
  emptySubtext: {
    color: 'rgba(245, 241, 234, 0.75)',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
  },
  confirmBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.7)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  confirmCard: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: EVENTS_BACKGROUND,
    borderRadius: 18,
    borderWidth: 2,
    borderColor: EVENTS_ACCENT,
    padding: 22,
    gap: 12,
  },
  confirmTitle: {
    color: EVENTS_ACCENT,
    fontSize: 20,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
  },
  confirmMessage: {
    color: EVENTS_ACCENT,
    fontSize: 15,
    lineHeight: 22,
    fontFamily: 'VastagoGrotesk',
  },
  confirmError: {
    color: '#ff7b7b',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
  },
  confirmActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 6,
  },
  confirmButton: {
    minWidth: 90,
    minHeight: 42,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    paddingHorizontal: 16,
  },
  cancelDeleteButton: {
    borderWidth: 1,
    borderColor: EVENTS_ACCENT,
  },
  cancelDeleteText: {
    color: EVENTS_ACCENT,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  confirmDeleteButton: {
    backgroundColor: '#b62b35',
  },
  confirmDeleteText: {
    color: '#fff',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
  },
});
